import React, { useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Tooltip } from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { useProfile } from '../context/ProfileContext';
import { useUserRole } from '../hooks/useUserRole';
import DarkMode from './Darkbtn';
import NotificationDropdownItem from './NotificationDropdownItem';
import {
    NOTIFICACOES_POR_USUARIO,
    Notificacao,
    NotificacoesPorUsuarioData,
    NotificacoesPorUsuarioVars,
    MARCAR_NOTIFICACOES_LIDAS,
    MarcarNotificacoesLidasData,
} from '../services/notificacaoService';

import 'bootstrap-icons/font/bootstrap-icons.css';
import Appfooter from './Appfooter';

// import { SearchOutline } from 'react-ionicons';

interface UsuarioAutenticado {
    id: string;
    nome: string;
    imagemUrl?: string | null;
    role?: string;
    atleta?: {
        id: string;
    } | null;
}

const Header: React.FC = () => {
    const [uiState, setUiState] = useState({
        isOpen: false,
        isActive: false,
        isNoti: false,
    });
    const [termoPesquisa, setTermoPesquisa] = useState('');
    const [erroMinChars, setErroMinChars] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const auth = useAuth();
    const { currentProfile } = useProfile();
    const { isAdminLocal, isAtleta } = useUserRole();

    const user: UsuarioAutenticado | null =
        (auth?.user as UsuarioAutenticado | null) ||
        (typeof window !== 'undefined'
            ? JSON.parse(localStorage.getItem('user') || 'null') as UsuarioAutenticado | null
            : null);
    const usuarioId = user?.id ?? '';
    const isAdmin = user?.role === 'ADMIN';

    const [fetchNotifications, { data: notiData, loading: notiLoading }] =
        useLazyQuery<NotificacoesPorUsuarioData, NotificacoesPorUsuarioVars>(
            NOTIFICACOES_POR_USUARIO,
            { fetchPolicy: 'no-cache' }
        );

    const [marcarTodasLidas] = useMutation<MarcarNotificacoesLidasData>(
        MARCAR_NOTIFICACOES_LIDAS
    );

    var atletaId = (auth?.user as { atleta?: { id?: string } } | null)?.atleta?.id;
    if (!atletaId) {
        const userStr = localStorage.getItem('user');
        try {
            const userObj = userStr ? JSON.parse(userStr) : null;
            atletaId = userObj?.atleta?.id || '';
        } catch {
            atletaId = '';
        }
    }

    const toggleState = (key: keyof typeof uiState) => {
        setUiState(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const navClass = uiState.isOpen ? 'nav-active' : '';
    const buttonClass = uiState.isOpen ? 'active' : '';
    const searchClass = uiState.isActive ? 'show' : '';
    const notiClass = uiState.isNoti ? 'show' : '';
    const notifications: Notificacao[] =
        notiData?.notificacoesPorUsuario.items ?? [];

    const notificationsDropdown = (
        <>
            <div className={`dropdown-menu p-4 right-0 rounded-xxl border-0 shadow-lg ${notiClass}`}>
                <h4 className="fw-700 font-xss mb-4">Notificações</h4>
                {notiLoading ? (
                    <div>Carregando...</div>
                ) : (notifications?.length ?? 0) > 0 ? (
                    notifications.map(n => <NotificationDropdownItem key={n.id} notification={n} />)
                ) : (
                    <div className="text-center text-muted">Nenhuma notificação encontrada.</div>
                )}

                {/* Clique fora para fechar */}
                {uiState.isNoti && (
                    <div
                    style={{
                        position: 'fixed',
                        inset: 0,
                        zIndex: 1000,
                        background: 'transparent',
                        pointerEvents: 'auto',
                    }}
                    onClick={() => setUiState(prev => ({ ...prev, isNoti: false }))}
                    tabIndex={-1}
                    aria-hidden="true"
                    />
                )}
                <style>{`
                    .dropdown-menu.right-0 {
                    top: 60px !important;
                    right: 0;
                    left: auto;
                    }
                    @media (max-width: 768px) {
                    .dropdown-menu.right-0 {
                        top: 50px !important;
                    }
                    }
                `}</style>
            </div>
        </>
    )

    const toggleNotifications = () => {
        toggleState('isNoti');
        if (!uiState.isNoti && usuarioId) {
            fetchNotifications({ variables: { page: 0, size: 5 } });
            marcarTodasLidas();
        }
    };

    const handlePesquisaSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const termo = termoPesquisa.trim();
        if (termo.length < 3) {
            setErroMinChars(true);
            return;
        }
        setErroMinChars(false);
        navigate(`/search?q=${encodeURIComponent(termo)}`);
    };

    const getProfileText = (): string => {
        if (isAdmin) {
            return 'Admin';
        } else if (currentProfile?.tipo === 'ATLETA') {
            return user?.nome || 'Atleta';
        } else if (currentProfile?.tipo === 'LIGA') {
            return currentProfile.nome;
        }
        return '';
    };

    const getProfileAvatarImg = (): string => {
        if (currentProfile?.tipo === 'LIGA' && currentProfile.imagemUrl) {
            return currentProfile.imagemUrl;
        }
        if (currentProfile?.tipo === 'LIGA') {
            return '/assets/images/rede-beach.png';
        }
        return user?.imagemUrl || '/assets/images/user.png';
    };

    const notificationsControl = (
        <>
            {/* Botão do sino */}
            <span
            className={`p-2 pointer text-center ms-auto menu-icon ${notiClass}`}
            onClick={toggleNotifications}
            >
            {notiData?.notificacoesPorUsuario.items?.some(n => !n.lida) && (
                <span className="dot-count bg-warning"></span>
            )}
            <i className="bi bi-bell font-xl text-white"></i>
            </span>

            {/* Dropdown de notificações */}
            {notificationsDropdown}
        </>
    );          

    return (
        <div className="nav-header shadow-xs border-0" style={{ backgroundColor: 'var(--bs-primary)' }}>
            <div className="nav-top flex justify-content-between">
                <div>
                    <Link to={user?.id ? "/home" : "/"} className='px-5'>
                        <img src="/assets/images/logo-inplay.svg" alt="InPlay Logo" className="nav-logo" style={{ height: 40 }} />
                    </Link>
                    {/* <Link to="/defaultmessage" className="mob-menu ms-auto me-2 chat-active-btn">
                        <i className="bi bi-chat text-grey-900 font-sm btn-round-md bg-greylight"></i>
                    </Link>
                    <Link to="/defaultvideo" className="mob-menu me-2">
                        <i className="bi bi-camera-video text-grey-900 font-sm btn-round-md bg-greylight"></i>
                    </Link> */}

                </div>

                
                <div>
                    <span
                        onClick={toggleNotifications}
                        className="me-2 mob-menu"
                    >
                        <i className="bi bi-bell text-white font-xxl btn-round-md"></i>
                    </span>
                    {notificationsDropdown}
                    <span
                        onClick={() => toggleState('isOpen')}
                        className={`mob-menu me-0 ms-2 ${buttonClass}`}
                    >
                        <i className="bi bi-list text-white font-xxl btn-round-md"></i>
                    </span>

                </div>
            </div>

            <form className="float-left header-search ms-3" onSubmit={handlePesquisaSubmit}>
                <Tooltip
                    title="Digite pelo menos 3 caracteres"
                    open={erroMinChars}
                    placement="bottom"
                    arrow
                >
                    <div className="form-group mb-0 icon-input">
                        <i className="bi bi-search font-sm text-grey-400"></i>
                        <input
                            type="text"
                            placeholder="Faça uma busca..."
                            className="bg-grey border-0 lh-32 pt-2 pb-2 ps-5 pe-3 font-xssss fw-500 rounded-xl w350 theme-dark-bg"
                            value={termoPesquisa}
                            onChange={(e) => { setTermoPesquisa(e.target.value); setErroMinChars(false); }}
                        />
                    </div>
                </Tooltip>
            </form>

            {/* Navigation Icons */}
            <div id="nav-icons-lg" className="align-items-center d-none d-lg-flex">

                <NavLink to="/home" className="p-2 text-center ms-3 menu-icon center-menu-icon">
                <i className="font-lg bg-greylight btn-round-lg theme-dark-bg " style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {/* <img src="/assets/fonts/feed-icon.svg" alt="Feed" style={{ width: 32, height: 32, padding: 0 }} /> */}
                    <i className="bi bi-grid-1x2 font-lg bg-greylight btn-round-lg theme-dark-bg text-grey-500" style={{ transform: 'rotate(90deg)' }}></i>
                </i>
                </NavLink>
                <NavLink to="/defaultevent" className="p-2 text-center ms-0 menu-icon center-menu-icon">
                <i className="bi bi-trophy font-lg bg-greylight btn-round-lg theme-dark-bg text-grey-500"></i>
                </NavLink>
                {currentProfile && (
                    <NavLink 
                        to={currentProfile.tipo === 'LIGA' ? '/liga/dashboard' : '/leagues'} 
                        className="p-2 text-center ms-0 menu-icon center-menu-icon"
                    >
                        <i className="bi bi-diagram-3 font-lg bg-greylight btn-round-lg theme-dark-bg text-grey-500"></i>
                    </NavLink>
                )}
                <NavLink to={isAdmin ? "/admin/payments" : "/athlete/payments"} className="p-2 text-center ms-0 menu-icon center-menu-icon">
                <i className="bi bi-credit-card-2-front font-lg bg-greylight btn-round-lg theme-dark-bg text-grey-500"></i>
                </NavLink>
                
            </div>


            {notificationsControl}

            
            {/* <DarkMode /> */}
            <div className="d-flex align-items-center ms-3" aria-label="Informações do usuário">
                <div className="d-flex flex-column me-2 text-white d-none d-md-flex">
                    <span className="font-sm fw-600">{getProfileText()}</span>
                </div>
                <Link to={ 
                    currentProfile?.tipo === 'LIGA' 
                        ? `/league/${currentProfile.referenciaId}` 
                        : atletaId 
                            ? `/userpage/${atletaId}` 
                            : '/defaultsettings'
                } className="p-0 menu-icon">
                    <img src={getProfileAvatarImg()} alt="user" className="w40 mt--1 rounded-pill" />
                </Link>
            </div>

            {/* Left Nav Menu */}
            <nav className={`navigation  ${navClass}`}>
            <div className="container ps-0 pe-0" style={{ overflowX: 'hidden', overflowY: 'auto', maxHeight: '100%' }}>
                <div className="nav-content">
                    {/* First Section */}
                    <div className="nav-wrap bg-white bg-transparent-card rounded-xxl shadow-xss pt-3 pb-1 mb-2 mt-2">
                        <div className="nav-caption fw-600 font-xssss "><span>Acesso </span>Rápido</div>
                        <ul className="mb-1 top-content">
                            <li>
                        <Link
                            to="/home"
                            className={`nav-content-bttn menu-round-background mx-0 px-2 open-font ${location.pathname === '/home' ? 'active-nav-item' : ''}`}
                        >
                            <i className="bi bi-grid-1x2 btn-round-md bg-primary me-3" style={{ transform: 'rotate(90deg)' }}></i>
                            <span>Feed de Atividades</span>
                        </Link></li>
                        <li><Link to="/defaultevent" className={`nav-content-bttn menu-round-background mx-0 px-2 open-font ${location.pathname === '/defaultevent' ? 'active-nav-item' : ''}` }><i className="bi bi-trophy btn-round-md bg-primary me-3"></i><span>Torneios</span></Link></li>
                        {isAtleta && (
                            <li>
                                <Link
                                    to="/quadras"
                                    className={`nav-content-bttn menu-round-background mx-0 px-2 open-font ${location.pathname.startsWith('/quadras') && !location.pathname.startsWith('/quadras/meus-agendamentos') && !location.pathname.startsWith('/quadras/pedido') && !location.pathname.startsWith('/quadras/pagamento') ? 'active-nav-item' : ''}`}
                                >
                                    <i className="bi bi-grid-3x3 btn-round-md bg-primary me-3"></i>
                                    <span>Aluguel de Quadras</span>
                                </Link>
                            </li>
                        )}
                        {isAtleta && (
                            <li>
                                <Link
                                    to="/quadras/meus-agendamentos"
                                    className={`nav-content-bttn menu-round-background mx-0 px-2 open-font ${location.pathname.startsWith('/quadras/meus-agendamentos') || location.pathname.startsWith('/quadras/pedido') || location.pathname.startsWith('/quadras/pagamento') ? 'active-nav-item' : ''}`}
                                >
                                    <i className="bi bi-calendar-check btn-round-md bg-primary me-3"></i>
                                    <span>Meus Agendamentos</span>
                                </Link>
                            </li>
                        )}
                        {isAdminLocal && (
                            <li>
                                <Link
                                    to="/admin/quadras"
                                    className={`nav-content-bttn menu-round-background mx-0 px-2 open-font ${location.pathname.startsWith('/admin/quadras') ? 'active-nav-item' : ''}`}
                                >
                                    <i className="bi bi-grid-3x3-gap btn-round-md bg-primary me-3"></i>
                                    <span>Gestão de Quadras</span>
                                </Link>
                            </li>
                        )}
                        {currentProfile && (
                            <li>
                                <Link
                                    to={currentProfile.tipo === 'LIGA' ? '/liga/dashboard' : '/leagues'}
                                    className={`nav-content-bttn menu-round-background mx-0 px-2 open-font ${location.pathname === (currentProfile.tipo === 'LIGA' ? '/liga/dashboard' : '/leagues') ? 'active-nav-item' : ''}`}
                                >
                                    <i className="bi bi-diagram-3 btn-round-md bg-primary me-3"></i>
                                    <span>Liga</span>
                                </Link>
                            </li>
                        )}
                        <li><Link to={isAdmin ? "/admin/payments" : "/athlete/payments"} className={`nav-content-bttn menu-round-background mx-0 px-2 open-font ${location.pathname === (isAdmin ? '/admin/payments' : '/athlete/payments') ? 'active-nav-item' : ''}` }><i className="bi bi-credit-card-2-front btn-round-md bg-primary me-3"></i><span>Pagamentos</span></Link></li>
                        {atletaId || currentProfile?.tipo === 'LIGA' ? (
                            <li>
                                <Link
                                    id="perfil-link"
                                    to={
                                        currentProfile?.tipo === 'LIGA'
                                            ? `/league/${currentProfile.referenciaId}`
                                            : atletaId
                                                ? `/userpage/${atletaId}`
                                                : '/userpage'
                                    }
                                    className={`nav-content-bttn menu-round-background mx-0 px-2 open-font ${
                                        location.pathname.startsWith('/userpage') ||
                                        (currentProfile?.tipo === 'LIGA' && location.pathname === `/league/${currentProfile.referenciaId}`)
                                            ? 'active-nav-item'
                                            : ''
                                    }`}
                                >
                                    <i className="bi bi-person btn-round-md bg-primary me-3"></i>
                                    <span>Perfil</span>
                                </Link>
                            </li>
                        ) : null}
                        </ul>
                    </div>

                    {/* Second Section */}
                    {isAdmin && <div className="nav-wrap bg-white bg-transparent-card rounded-xxl shadow-xss pt-3 pb-1 mb-2">
                        <div className="nav-caption fw-600 font-xssss text-grey-500">Opções do administrador</div>
                        <ul className="mb-1">
                        <li><Link to="/admin/financeiro" className={`nav-content-bttn menu-round-background open-font h-auto pt-2 pb-2 ${location.pathname === '/admin/financeiro' ? 'active-nav-item' : ''}` }><i className="font-sm bi bi-cash-coin me-3 text-grey-500"></i><span>Financeiro</span></Link></li>
                        <li><Link to="/admin/atletas" className={`nav-content-bttn menu-round-background open-font h-auto pt-2 pb-2 ${location.pathname === '/admin/atletas' ? 'active-nav-item' : ''}` }><i className="font-sm bi bi-people me-3 text-grey-500"></i><span>Gerenciar Atletas</span></Link></li>
                        <li><Link to="/admin/tournaments" className={`nav-content-bttn menu-round-background open-font h-auto pt-2 pb-2 ${location.pathname === '/admin/tournaments' ? 'active-nav-item' : ''}` }><i className="font-sm bi bi-trophy me-3 text-grey-500"></i><span>Gerenciar Torneios</span></Link></li>
                        <li><Link to="/admin/ligas" className={`nav-content-bttn menu-round-background open-font h-auto pt-2 pb-2 ${location.pathname === '/admin/ligas' ? 'active-nav-item' : ''}` }><i className="font-sm bi bi-diagram-3 me-3 text-grey-500"></i><span>Gerenciar Ligas</span></Link></li>
                        <li><Link to="/admin/cupons" className={`nav-content-bttn menu-round-background open-font h-auto pt-2 pb-2 ${location.pathname === '/admin/cupons' ? 'active-nav-item' : ''}` }><i className="font-sm bi bi-ticket me-3 text-grey-500"></i><span>Cupons de desconto</span></Link></li>
                        <li><Link to="/admin/usuarios" className={`nav-content-bttn menu-round-background open-font h-auto pt-2 pb-2 ${location.pathname === '/admin/usuarios' ? 'active-nav-item' : ''}`}><i className="font-sm bi bi-person-gear me-3 text-grey-500"></i><span>Gerenciar Admins</span></Link></li>
                        </ul>
                    </div>
                    }

                    {/* Third Section */}
                    <div className="nav-wrap bg-white bg-transparent-card rounded-xxl shadow-xss pt-3 pb-1">
                        <div className="nav-caption fw-600 font-xssss text-grey-500">Conta</div>
                        <ul className="mb-1">
                        <li><Link to="/defaultsettings" className={`nav-content-bttn menu-round-background open-font h-auto pt-2 pb-2 ${location.pathname === '/defaultsettings' ? 'active-nav-item' : ''}` }><i className="font-sm bi bi-gear me-3 text-grey-500"></i><span>Configurações</span></Link></li>
                        </ul>
                    </div>
                </div>
            </div>
            </nav>

            {/* Mobile Search Bar */}
            <div className={`app-header-search ${searchClass}`}>
            <form className="search-form" onSubmit={handlePesquisaSubmit}>
                <Tooltip
                    title="Digite pelo menos 3 caracteres"
                    open={erroMinChars}
                    placement="bottom"
                    arrow
                >
                    <div className="form-group searchbox mb-0 border-0 p-1">
                        <input
                            type="text"
                            className="form-control border-0"
                            placeholder="Faça uma busca..."
                            value={termoPesquisa}
                            onChange={(e) => { setTermoPesquisa(e.target.value); setErroMinChars(false); }}
                        />
                        <i className="input-icon">

                        </i>
                        <span className="ms-1 mt-1 d-inline-block close searchbox-close">
                            <i className="ti-close font-xs" onClick={() => toggleState('isActive')}></i>
                        </span>
                    </div>
                </Tooltip>
            </form>
            </div>
            <Appfooter />
        </div>
    );
};

export default Header;
